// Phase 14 — Unified Approvals Inbox
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, Check, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import PageHeader from "@/components/PageHeader";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

const TYPE_COLOR = {
  order: "bg-sky-100 text-sky-800 border-sky-200",
  leave: "bg-amber-100 text-amber-800 border-amber-200",
  payment: "bg-emerald-100 text-emerald-800 border-emerald-200",
  claim: "bg-purple-100 text-purple-800 border-purple-200",
  sale_return: "bg-rose-100 text-rose-800 border-rose-200",
  expense: "bg-indigo-100 text-indigo-800 border-indigo-200",
};

export default function ApprovalsInbox() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [entityType, setEntityType] = useState("");
  const [decide, setDecide] = useState(null); // {approval, action, notes}
  const [busy, setBusy] = useState(false);

  async function reload() {
    setLoading(true);
    try {
      const p = entityType ? `?entity_type=${entityType}` : "";
      const { data } = await api.get(`/approvals/inbox${p}`);
      setItems(data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }
  useEffect(() => { reload(); }, [entityType]);

  async function perform() {
    if (decide.action === "reject" && !(decide.notes || "").trim()) {
      toast.error("Reason required"); return;
    }
    setBusy(true);
    try {
      await api.post(`/approvals/${decide.approval.id}/${decide.action}`, { notes: decide.notes || null });
      toast.success(decide.action === "approve" ? "Approved" : "Rejected");
      setDecide(null); reload();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  }

  return (
    <div className="p-6 space-y-4" data-testid="approvals-inbox-page">
      <PageHeader
        breadcrumbs={[{ label: "Admin", to: "/" }, { label: "Orders" }, { label: "Approvals Inbox" }]}
        title="Approvals Inbox"
        subtitle="Everything waiting for your approval — orders, claims, leave and returns."
        favorite={{ key: "admin.approvals-inbox", type: "screen", label: "Approvals Inbox", route: "/approvals/inbox" }}
        testid="approvalsinbox-page-header"
      />

      <div className="flex items-center gap-3">
        <h1 className="text-2xl font-semibold">Approvals Inbox</h1>
        <Select value={entityType || "__all__"} onValueChange={(v) => setEntityType(v === "__all__" ? "" : v)}>
          <SelectTrigger className="w-48 h-8"><SelectValue placeholder="All entity types" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All types</SelectItem>
            {Object.keys(TYPE_COLOR).map(t => <SelectItem key={t} value={t}>{t.replace(/_/g, " ")}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex-1" />
        <Button variant="ghost" onClick={reload} data-testid="inbox-refresh"><RefreshCw className="w-4 h-4 mr-1" /> Refresh</Button>
      </div>

      {loading ? <div className="text-neutral-500 text-sm">Loading…</div>
       : items.length === 0 ? <div className="text-neutral-500 border border-dashed rounded-lg p-10 text-center">Nothing pending for you.</div>
       : items.map(a => (
        <div key={a.id} className="border rounded-lg p-3 bg-white flex items-center gap-3" data-testid={`approval-row-${a.id}`}>
          <span className={`px-2 py-0.5 rounded text-[11px] border ${TYPE_COLOR[a.entity_type] || "bg-neutral-100"}`}>{a.entity_type}</span>
          <div className="min-w-0 flex-1">
            <div className="font-medium truncate">{a.config_name || "Approval"}</div>
            <div className="text-xs text-neutral-500">
              step {a.current_step}/{a.total_steps} · entity_id: {a.entity_id.slice(0, 8)}…
              {a.current_timeout_ts && ` · timeout ${a.current_timeout_ts.slice(5, 16).replace("T", " ")}`}
            </div>
          </div>
          <Button size="sm" onClick={() => setDecide({ approval: a, action: "approve", notes: "" })} data-testid={`approval-approve-${a.id}`}>
            <Check className="w-3 h-3 mr-1" /> Approve
          </Button>
          <Button size="sm" variant="destructive" onClick={() => setDecide({ approval: a, action: "reject", notes: "" })} data-testid={`approval-reject-${a.id}`}>
            <X className="w-3 h-3 mr-1" /> Reject
          </Button>
        </div>
       ))
      }

      <Dialog open={!!decide} onOpenChange={(o) => { if (!o) setDecide(null); }}>
        <DialogContent data-testid="approval-decide-dialog">
          <DialogHeader>
            <DialogTitle>{decide?.action === "approve" ? "Approve step" : "Reject with reason"}</DialogTitle>
            <DialogDescription>
              {decide?.approval?.entity_type} · step {decide?.approval?.current_step}/{decide?.approval?.total_steps}
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label>Notes {decide?.action === "reject" && "*"}</Label>
            <Textarea rows={3} value={decide?.notes || ""} onChange={(e) => setDecide(d => ({ ...d, notes: e.target.value }))} data-testid="approval-notes-input" />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDecide(null)}>Cancel</Button>
            <Button onClick={perform} disabled={busy} data-testid="approval-confirm-btn">
              {busy ? "Working…" : "Confirm"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
